import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useToast,
} from '@/components/ui';
import { useUpdateProductSettings } from '@/hooks/usePriceSettings';
import type { ApiSubItem } from '@/lib/sales-metrics';

// Stable code prefix per API bundle product, matching the seeded catalogues
// (mfr_1..., calc_1...). New items continue the numbering.
const CODE_PREFIX: Record<string, string> = {
  API_MF_RESEARCH: 'mfr',
  API_CALCULATOR: 'calc',
};

interface ItemDraftRow {
  uid: number;
  /** Stable key stored in product_details.metadata; kept across edits. */
  code: string;
  name: string;
  price: string;
}

interface ApiSubItemsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productCode: string;
  productName: string;
  items: ApiSubItem[];
}

export const ApiSubItemsDialog = ({
  open,
  onOpenChange,
  productCode,
  productName,
  items,
}: ApiSubItemsDialogProps) => {
  const update = useUpdateProductSettings();
  const { toast } = useToast();
  const uidCounter = useRef(0);

  const freshUid = (): number => {
    uidCounter.current += 1;
    return uidCounter.current;
  };

  const [rows, setRows] = useState<ItemDraftRow[]>([]);

  useEffect(() => {
    if (!open) return;
    setRows(
      items.map((item) => ({
        uid: freshUid(),
        code: item.code,
        name: item.name,
        price: String(item.price),
      })),
    );
  }, [open, items]);

  const nextItemCode = (current: ItemDraftRow[]): string => {
    const prefix = CODE_PREFIX[productCode] ?? 'item';
    const maxSuffix = current.reduce((max, row) => {
      const match = new RegExp(`^${prefix}_(\\d+)$`).exec(row.code);
      const n = match ? Number(match[1]) : 0;
      return n > max ? n : max;
    }, 0);
    return `${prefix}_${maxSuffix + 1}`;
  };

  const addRow = (): void => {
    setRows((prev) => [
      ...prev,
      {
        uid: freshUid(),
        code: nextItemCode(prev),
        name: '',
        price: '',
      },
    ]);
  };

  const removeRow = (uid: number): void => {
    setRows((prev) => prev.filter((row) => row.uid !== uid));
  };

  const setRowField = (uid: number, field: 'name' | 'price', value: string): void => {
    setRows((prev) => prev.map((row) => (row.uid === uid ? { ...row, [field]: value } : row)));
  };

  const onSave = (): void => {
    if (rows.length === 0) {
      toast.error(
        'At least one item is required - delete the product instead of emptying its list',
      );
      return;
    }
    const parsed: ApiSubItem[] = [];
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const name = row.name.trim();
      const price = Number(row.price);
      if (!name) {
        toast.error(`Item ${i + 1} needs a name`);
        return;
      }
      if (!Number.isFinite(price) || price < 0 || !Number.isInteger(price)) {
        toast.error(`"${name}" needs a whole-rupee price of 0 or more`);
        return;
      }
      parsed.push({ code: row.code, name, price });
    }

    update.mutate(
      { apiSubItems: { [productCode]: parsed } },
      {
        onSuccess: () => {
          toast.success(`${productName} items saved`);
          onOpenChange(false);
        },
        onError: (err) => toast.error(err.message || 'Could not save items'),
      },
    );
  };

  const total = rows.reduce((sum, row) => {
    const n = Number(row.price);
    return Number.isFinite(n) && n > 0 ? sum + n : sum;
  }, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{`${productName} - Items`}</DialogTitle>
          <DialogDescription>
            The Sales RM picks from this list in the wizard; the product price is the sum of the
            selected items. Prices are in whole rupees.
          </DialogDescription>
        </DialogHeader>

        <div className="flex shrink-0 items-center justify-between border-b border-line pb-2">
          <span className="text-xs text-ink/50">
            {`${rows.length} item${rows.length === 1 ? '' : 's'}`}
          </span>
          <Button variant="outline" size="sm" onClick={addRow} className="flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" />
            Add item
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="flex flex-col gap-1.5 py-1">
            {rows.map((row, index) => (
              <div
                key={row.uid}
                className="grid grid-cols-[1.5rem_1fr_7rem_2rem] items-center gap-2"
              >
                <span className="text-[11px] font-semibold text-ink/40">{index + 1}.</span>
                <Input
                  placeholder="Item name"
                  value={row.name}
                  onChange={(e) => setRowField(row.uid, 'name', e.target.value)}
                  className="h-8 text-sm"
                />
                <Input
                  type="number"
                  min={0}
                  step={500}
                  placeholder="Price (₹)"
                  value={row.price}
                  onChange={(e) => setRowField(row.uid, 'price', e.target.value)}
                  className="h-8 text-right text-sm"
                  aria-label={`${row.name || `Item ${index + 1}`} price`}
                />
                <button
                  type="button"
                  onClick={() => removeRow(row.uid)}
                  aria-label={`Remove ${row.name || `item ${index + 1}`}`}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-danger transition-colors hover:bg-danger/10"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            {rows.length === 0 && (
              <p className="py-6 text-center text-sm text-ink/50">No items - add at least one.</p>
            )}
          </div>
        </div>

        <DialogFooter className="flex shrink-0 items-center justify-between gap-3 border-t border-line pt-3 sm:justify-between">
          <span className="text-sm text-ink/60">
            All-items total:{' '}
            <span className="font-bold text-ink">{`₹${total.toLocaleString('en-IN')}`}</span>
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={update.isPending}
            >
              Cancel
            </Button>
            <Button onClick={onSave} disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save items'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
